<script setup lang="ts">
  import { ref, useAttrs, watch } from 'vue'

  interface Props {
    min?: number
    max?: number
  }

  const props = withDefaults(defineProps<Props>(), {
    min: Number.MIN_SAFE_INTEGER,
    max: Number.MAX_SAFE_INTEGER,
  })

  const model = defineModel<number | null>({ default: null })

  const attrs = useAttrs()

  const rawValue = ref(model.value?.toString() ?? '')

  watch(model, (val) => {
    if (val === null) {
      rawValue.value = String(props.min)
      model.value = props.min
      return
    }

    const clamped = clamp(String(val))
    rawValue.value = clamped !== null ? String(clamped) : String(props.min)
    model.value = clamped
  })

  watch(rawValue, (val) => {
    const sanitized = sanitize(val)
    const clamped = clamp(sanitized)

    rawValue.value = clamped !== null ? String(clamped) : String(props.min)
    model.value = clamped
  })

  const sanitize = (value: string) => {
    value = String(value) //required or we get errors when doing value.replace
    const sign = value[0] === '-' ? '-' : ''
    const digits = value.replace(/\D/g, '')
    return digits ? sign + digits : sign
  }

  const clamp = (value: string) =>
    value === '' || value === '-' ? null : Math.min(props.max, Math.max(props.min, Number(value)))
</script>

<template>
  <input type="text" inputmode="numeric" v-bind="attrs" v-model="rawValue" />
</template>
